import { Dispatch, useEffect, useRef } from 'react'
import VendNumber from 'vend-number'
import {
  getFromStorage,
  removeFromStorage,
  saveToStorage,
  trackUserEvent,
} from '@vend/utilities'
import {
  PAYMENT_STATUS,
  TERMINAL_EVENT,
  TerminalEventMonitoring,
} from '@lightspeed-payments/payments-terminal-sdk'
import { PaymentEvent } from '../use-lspay-payment-server-reducer/types'
import {
  cancelTransaction,
  CancelTransactionResponse,
  createTransaction,
  isTransactionCompleted,
} from '../../../../../helpers/lspayTransaction'
import {
  LSPAY_ERROR,
  LSPAY_TRANSACTION_STATUS,
  SWIPER_CANCEL_REASON,
  SWIPER_ERROR_CODES,
  TRANSACTION_TYPE,
} from '../../../../../helpers/lspayConstants'
import {
  getPaymentDeclinedError,
  isCardSpecificDecline,
  isRetryableError,
} from '../../../../../helpers/errorChecking'
import {
  ACTIVE_LSPAY_TRANSACTION,
  ActiveLSPayTransaction,
} from '../../../../../../../../constants/payments'
import { withRetry } from '../../../../../helpers/withRetry'
import { WEB_REGISTER } from '../../../../../../../web-register.constant'
import { TAG, trackLSPayServerError } from '../../../../../hooks/errorTracking'
import { SwiperError, TerminalInfo } from '../../../../../helpers/lspay.types'
import { getTotalSurchargeAmountFromTenders } from '../../../../../../../../functions/payments'
import { sendTerminalEvent } from '../../../../../helpers/terminalMonitoringEventDispatcher'
import {
  LSPAY_TERMINAL_EVENT,
  sendLSPayTerminalEvent,
} from '../../../../../helpers/terminalMonitoring'
import { useErrorMessages } from '../../../../../hooks/useErrorMessages'

interface createTransactionOptions {
  amount: VendNumber
  currency: string
  registerId: string
  registerSaleId: string
  retailerPaymentTypeId: string
  registerSalePaymentId: string
  terminalMonitoringInstance: TerminalEventMonitoring | null
  dispatch: Dispatch<PaymentEvent>
  withSurcharge?: boolean
  tipEligibleAmount?: VendNumber
  setCancelProcessing: (cancelProcessing: () => void) => void
  setSwiperError: (swiperError: SwiperError) => void
  terminalInfo: TerminalInfo
}

export const useCreateTransaction = ({
  amount,
  currency,
  registerId,
  registerSaleId,
  retailerPaymentTypeId,
  registerSalePaymentId,
  terminalMonitoringInstance,
  dispatch,
  withSurcharge,
  tipEligibleAmount,
  setCancelProcessing,
  setSwiperError,
  terminalInfo,
}: createTransactionOptions) => {
  const { deviceId, deviceModel } = terminalInfo
  const getErrorMessages = useErrorMessages()
  // Guard against duplicate createTransaction calls. If the effect re-runs
  // (e.g. due to a dependency change or parent re-render) while this component
  // instance is still mounted, skip the second call. The ref resets naturally
  // when CreatingTransaction unmounts and a fresh instance mounts on retry.
  const hasCreatedRef = useRef(false)
  useEffect(() => {
    if (hasCreatedRef.current) return
    hasCreatedRef.current = true
    const transaction = async () => {
      // An active transaction already exists for this sale with a different
      // registerSalePaymentId, so creating a new one overwrites the recovery record.
      const existingActiveTransaction = getFromStorage<ActiveLSPayTransaction>(
        ACTIVE_LSPAY_TRANSACTION
      )
      if (
        existingActiveTransaction &&
        existingActiveTransaction.registerSaleId === registerSaleId &&
        existingActiveTransaction.registerSalePaymentId !==
          registerSalePaymentId
      ) {
        const overwriteContext = {
          registerSaleId,
          previousRegisterSalePaymentId:
            existingActiveTransaction.registerSalePaymentId,
          previousPaymentIntentId: existingActiveTransaction.paymentIntentId,
          newRegisterSalePaymentId: registerSalePaymentId,
          previousAmount: Number(existingActiveTransaction.amount),
          newAmount: amount.toNumber(),
          amountsMatch: amount.eq(existingActiveTransaction.amount),
          currency,
        }
        sendLSPayTerminalEvent(
          terminalMonitoringInstance,
          LSPAY_TERMINAL_EVENT.ACTIVE_TRANSACTION_OVERWRITTEN,
          overwriteContext
        )
      }

      // Save the transaction details to local storage for recovery
      const activeLSPayTransaction: ActiveLSPayTransaction = {
        registerSaleId,
        registerSalePaymentId,
        amount: amount.toString(),
      }
      saveToStorage(ACTIVE_LSPAY_TRANSACTION, activeLSPayTransaction)

      try {
        const response = await createTransaction({
          amount,
          currency,
          registerId,
          retailerPaymentTypeId,
          registerSalePaymentId,
          withSurcharge,
          ...(tipEligibleAmount ? { tipEligibleAmount } : {}), // only include tipEligiblAmount if it is provided
          metadata: { register_sale_id: registerSaleId },
        })

        const { payment_intent_id: paymentIntentId } = response

        // Update the transaction details to local storage for recovery with paymentIntentId
        const updatedActiveLSPayTransaction: ActiveLSPayTransaction = {
          ...activeLSPayTransaction,
          paymentIntentId,
        }
        saveToStorage(ACTIVE_LSPAY_TRANSACTION, updatedActiveLSPayTransaction)

        if (response.status === LSPAY_TRANSACTION_STATUS.PROCESSING) {
          // set a func to cancel the processing anywhere in the LSPay modal
          // current use case is that the user switches to CNP tab, and call this func to cancel the processing
          const cancelProcessing = () => {
            withRetry<CancelTransactionResponse>(
              () =>
                cancelTransaction({
                  registerId,
                  paymentIntentId,
                  registerSalePaymentId,
                }),
              'cancelProcessingInCNP'
            )
              .then(transaction => {
                terminalMonitoringInstance?.sendEvent(TERMINAL_EVENT.COMPLETE, {
                  payment: {
                    id: paymentIntentId,
                    status: PAYMENT_STATUS.CANCELLED,
                    reason: 'Requested by customer',
                  },
                  terminal: {
                    id: deviceId,
                    model: deviceModel,
                  },
                  transactionType: TRANSACTION_TYPE.PAYMENT,
                })
                // remove the active lspay transaction from storage for cases when the user switches to CNP tab
                // and transaction is considered cancelled.
                removeFromStorage(ACTIVE_LSPAY_TRANSACTION)

                if (transaction.status !== LSPAY_TRANSACTION_STATUS.CANCELLED) {
                  trackUserEvent(
                    WEB_REGISTER.DISPLAY_NAME,
                    'Cancel processing LSD not CANCELLED in CNP',
                    {
                      registerId,
                      paymentIntentId,
                      registerSalePaymentId,
                    }
                  )
                }
              })
              .catch((error: SwiperError) => {
                sendTerminalEvent({
                  terminalMonitoringInstance,
                  intentId: paymentIntentId,
                  terminal: {
                    id: deviceId,
                    model: deviceModel,
                  },
                  reason:
                    'Unable to cancel payment processing after the user has switched to card-not-present tab',
                  swiperError: error,
                  transactionType: TRANSACTION_TYPE.PAYMENT,
                })
                trackUserEvent(
                  WEB_REGISTER.DISPLAY_NAME,
                  'Cancel processing LSD failed in CNP',
                  {
                    registerId,
                    paymentIntentId,
                    registerSalePaymentId,
                  }
                )
              })
          }
          setCancelProcessing(() => cancelProcessing)
          dispatch({
            type: 'START_POLLING',
            paymentIntentId,
          })
          return
        }
        if (response.status === LSPAY_TRANSACTION_STATUS.DECLINED) {
          sendTerminalEvent({
            terminalMonitoringInstance,
            intentId: paymentIntentId,
            status: response.status,
            reason: response.reason,
            terminal: {
              id: deviceId,
              model: deviceModel,
            },
            transactionType: TRANSACTION_TYPE.PAYMENT,
          })
          dispatch({
            type: 'DECLINED',
            errorMessage: getErrorMessages(
              getPaymentDeclinedError(response.reason),
              isCardSpecificDecline(response.reason)
            ),
            paymentIntentId: response.payment_intent_id,
          })
          return
        }
        if (isTransactionCompleted(response)) {
          terminalMonitoringInstance?.sendEvent(TERMINAL_EVENT.COMPLETE, {
            payment: {
              id: paymentIntentId,
              status: PAYMENT_STATUS.APPROVED,
            },
            terminal: {
              id: deviceId,
              model: deviceModel,
            },
            transactionType: TRANSACTION_TYPE.PAYMENT,
            ...(getTotalSurchargeAmountFromTenders(response.transaction.tenders)
              ? { surcharging: true }
              : {}),
          })
          dispatch({
            type: 'POLL_RESULT_OK',
            transaction: response.transaction,
          })
          return
        }
        if (response.status === LSPAY_TRANSACTION_STATUS.AUTHORISED) {
          dispatch({
            type: 'AUTHORISED',
            paymentIntentId: response.payment_intent_id,
          })
          return
        }
        // For Cancellation due to cancelled intent
        if (
          response.status === LSPAY_TRANSACTION_STATUS.CANCELLED &&
          response.reason === SWIPER_CANCEL_REASON.INTENT_CANCELLED
        ) {
          // remove the active lspay transaction from storage for cases where the createTransaction is in pending state
          // when the user switches to CNP tab and later resolves with a cancelled intent.
          removeFromStorage(ACTIVE_LSPAY_TRANSACTION)

          terminalMonitoringInstance?.sendEvent(TERMINAL_EVENT.COMPLETE, {
            payment: {
              id: paymentIntentId,
              status: PAYMENT_STATUS.ERROR,
              reason: 'Cancelled by system (terminal false negative error)',
            },
            terminal: {
              id: deviceId,
              model: deviceModel,
            },
            transactionType: TRANSACTION_TYPE.PAYMENT,
          })
          dispatch({
            type: 'CANCELLED',
            errorMessage: getErrorMessages(
              LSPAY_ERROR.CANCEL_PAYMENT_WITH_INTENT_CANCELLED
            ),
            paymentIntentId: response.payment_intent_id,
            reason: response.reason,
          })
          return
        }
        if (response.status === LSPAY_TRANSACTION_STATUS.CANCELLED) {
          terminalMonitoringInstance?.sendEvent(TERMINAL_EVENT.COMPLETE, {
            payment: {
              id: paymentIntentId,
              status: PAYMENT_STATUS.ERROR,
              reason: 'Cancelled by system (' + response.reason + ')',
            },
            terminal: {
              id: deviceId,
              model: deviceModel,
            },
            transactionType: TRANSACTION_TYPE.PAYMENT,
          })
          dispatch({
            type: 'CANCELLED',
            errorMessage: getErrorMessages(LSPAY_ERROR.CANCEL_PAYMENT),
            paymentIntentId: response.payment_intent_id,
            reason: response.reason,
          })
          return
        }
      } catch (error: any) {
        // track the error
        trackLSPayServerError(
          '[lspay-sever-driven] failed to create transaction',
          error,
          {
            registerSalePaymentId,
            registerSaleId,
          },
          TAG.payment
        )
        const shouldNotRetry = !!error && !isRetryableError(error.code)
        if (shouldNotRetry) {
          // remove the active lspay transaction from storage for cases where the createTransaction got non-retryable error
          // there is no point in recovering the transaction in this case
          removeFromStorage(ACTIVE_LSPAY_TRANSACTION)
        }
        if (error?.code === SWIPER_ERROR_CODES.LSPAY_AMOUNT_TOO_SMALL) {
          setSwiperError(error)
          return
        }
        const isPairingNotFound =
          error?.code === SWIPER_ERROR_CODES.CODE_REGISTER_PAIRING_NOT_FOUND
        const lspayError = isPairingNotFound
          ? LSPAY_ERROR.PAIRING_MISSING
          : LSPAY_ERROR.DEFAULT_PAYMENT

        dispatch({
          type: 'ERROR',
          errorMessage: getErrorMessages(lspayError),
          swiperError: error,
          disableRetry: shouldNotRetry,
        })
      }
    }
    transaction()
  }, [
    amount,
    currency,
    dispatch,
    registerId,
    registerSaleId,
    retailerPaymentTypeId,
    registerSalePaymentId,
    terminalMonitoringInstance,
    setCancelProcessing,
    setSwiperError,
    withSurcharge,
    deviceId,
    deviceModel,
    getErrorMessages,
    tipEligibleAmount,
  ])
}
